import { useMemo, useState } from "react";

import { t } from "../../i18n";
import { useApp, useExcalidrawSetAppState } from "../App";
import { InlineIcon } from "../InlineIcon";
import { ArrowRightIcon } from "../icons";
import { TTDDialogInput } from "./TTDDialogInput";
import { TTDDialogOutput } from "./TTDDialogOutput";
import { TTDDialogPanel } from "./TTDDialogPanel";
import { TTDDialogPanels } from "./TTDDialogPanels";
import {
  type EducationPromptInput,
  type OnEducationSubmitRetValue,
  insertEducationContent,
} from "./common";

const MIN_TOPIC_LENGTH = 4;

const formatOptions: Array<EducationPromptInput["format"]> = [
  "lesson",
  "diagram",
  "quiz",
];

export const EducationTab = ({
  onSubmit,
}: {
  onSubmit: (payload: EducationPromptInput) => Promise<OnEducationSubmitRetValue>;
}) => {
  const app = useApp();
  const setAppState = useExcalidrawSetAppState();

  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("Middle school");
  const [format, setFormat] = useState<EducationPromptInput["format"]>("lesson");
  const [includeImage, setIncludeImage] = useState(true);
  const [guidance, setGuidance] = useState(
    "Keep it concise, focused on one concept, and classroom-ready.",
  );
  const [result, setResult] = useState<OnEducationSubmitRetValue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const promptPreview = useMemo(() => {
    if (!topic.trim()) {
      return "";
    }
    return `${topic} — ${audience} — ${format}${
      guidance ? `\nNotes: ${guidance}` : ""
    }\n${includeImage ? "Include an illustrative image." : "Text only."}`;
  }, [audience, format, guidance, includeImage, topic]);

  const handleGenerate = async () => {
    if (topic.trim().length < MIN_TOPIC_LENGTH || loading) {
      setError(
        topic.trim().length < MIN_TOPIC_LENGTH
          ? "Prompt is too short"
          : null,
      );
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await onSubmit({
        topic: topic.trim(),
        audience: audience.trim(),
        format,
        includeImage,
        guidance: guidance.trim(),
      });

      if (response.error) {
        setError(response.error.message);
      }
      setResult(response);
    } catch (err: any) {
      setError(err?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = async () => {
    if (!result?.text && !result?.imageDataURL) {
      return;
    }
    await insertEducationContent({
      app,
      text: result.text ?? null,
      imageDataURL: result.imageDataURL ?? null,
    });
    setAppState({ openDialog: null });
  };

  return (
    <TTDDialogPanels>
      <TTDDialogPanel
        label={t("labels.prompt")}
        panelAction={{
          action: handleGenerate,
          label: "Generate",
          icon: ArrowRightIcon,
        }}
        onTextSubmitInProgess={loading}
        panelActionDisabled={topic.trim().length < MIN_TOPIC_LENGTH}
        renderBottomRight={() => {
          if (!topic.trim()) {
            return null;
          }
          const ratio = topic.trim().length / 240;
          return (
            <div
              style={{
                marginLeft: "auto",
                fontSize: 12,
                fontFamily: "monospace",
                color: ratio > 1 ? "var(--color-danger)" : undefined,
              }}
            >
              Length: {topic.trim().length}/240
            </div>
          );
        }}
      >
        <div className="ttd-dialog-desc">
          Generate classroom-ready content powered by Google Gemini. Provide a
          topic, audience, and format. We'll prepare text and an optional
          illustrative image you can place on the canvas.
        </div>
        <div className="ttd-dialog-inputs">
          <label className="ttd-dialog-label">
            Topic
            <input
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="Photosynthesis basics"
              className="ttd-dialog-input"
            />
          </label>
          <label className="ttd-dialog-label">
            Audience
            <input
              value={audience}
              onChange={(event) => setAudience(event.target.value)}
              placeholder="Grade 8"
              className="ttd-dialog-input"
            />
          </label>
          <div className="ttd-dialog-row">
            <label className="ttd-dialog-label" style={{ flex: 1 }}>
              Format
              <select
                value={format}
                onChange={(event) =>
                  setFormat(event.target.value as EducationPromptInput["format"])
                }
                className="ttd-dialog-input"
              >
                {formatOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="ttd-dialog-checkbox">
              <input
                type="checkbox"
                checked={includeImage}
                onChange={(event) => setIncludeImage(event.target.checked)}
              />
              Include illustrative image
            </label>
          </div>
          <label className="ttd-dialog-label">
            Guidance (optional)
            <TTDDialogInput
              input={guidance}
              onChange={(event) => setGuidance(event.target.value)}
              placeholder="Tone, constraints, bullet points, or style guidance"
            />
          </label>
          {promptPreview && (
            <div className="ttd-dialog-preview">
              <div className="ttd-dialog-preview__label">Preview</div>
              <pre className="ttd-dialog-preview__content">{promptPreview}</pre>
            </div>
          )}
        </div>
      </TTDDialogPanel>

      <TTDDialogPanel
        label="Preview"
        panelAction={{
          action: handleInsert,
          label: "Insert",
          icon: ArrowRightIcon,
        }}
        panelActionDisabled={loading || (!result?.text && !result?.imageDataURL)}
        onTextSubmitInProgess={loading}
      >
        {error && (
          <div className="ttd-dialog-desc" style={{ color: "var(--color-danger)" }}>
            {error}
          </div>
        )}
        {!error && !loading && !result && (
          <div className="ttd-dialog-desc">Generated content will appear here.</div>
        )}
        <TTDDialogOutput
          canvasRef={null}
          error={null}
          loaded={true}
          renderFallback={() => (
            <div className="ttd-dialog-output__fallback">
              {result?.text && (
                <div className="ttd-dialog-generated-text">
                  <div className="ttd-dialog-preview__label">
                    <InlineIcon icon={ArrowRightIcon} /> Text
                  </div>
                  <pre className="ttd-dialog-preview__content">{result.text}</pre>
                </div>
              )}
              {result?.imageDataURL && (
                <div className="ttd-dialog-generated-image">
                  <div className="ttd-dialog-preview__label">
                    <InlineIcon icon={ArrowRightIcon} /> Image
                  </div>
                  <img
                    src={result.imageDataURL}
                    alt="Gemini generated"
                    style={{
                      maxWidth: "100%",
                      borderRadius: 8,
                      border: "1px solid var(--color-gray-20)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                    }}
                  />
                </div>
              )}
            </div>
          )}
        />
      </TTDDialogPanel>
    </TTDDialogPanels>
  );
};

EducationTab.displayName = "EducationTab";

